import React, { useEffect, useState } from 'react';
import { Clock, X, Zap } from 'lucide-react';
import { useFirm } from '../contexts/FirmContext';
import UpgradeModal from './UpgradeModal';

// Milestones at which the banner re-appears even if previously dismissed.
// Lawyers shouldn't be allowed to hide it forever — but the alternative of
// "always show" is annoying. Compromise: dismiss persists until the urgency
// level increases, at which point we show it again.
//
// Ordering matters — descending so we pick the highest milestone the user
// is at or under. e.g. 5 days left → milestone 7.
const MILESTONES = [14, 7, 3, 1, 0];

function milestoneFor(daysLeft) {
  for (const m of MILESTONES) {
    if (daysLeft <= m) return m;
  }
  return null;
}

function dismissKey(firmId) { return `lf_trial_banner_dismissed_${firmId || 'anon'}_v1`; }

export default function TrialBanner() {
  const { firm, isTrial, daysRemaining } = useFirm();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const currentMilestone = milestoneFor(daysRemaining);

  const [dismissedMilestone, setDismissedMilestone] = useState(null);
  useEffect(() => {
    if (!firm?.id) return;
    try {
      const raw = localStorage.getItem(dismissKey(firm.id));
      setDismissedMilestone(raw === null ? null : Number(raw));
    } catch { /* ignore */ }
  }, [firm?.id]);

  if (!isTrial || currentMilestone === null) return null;
  // If user already dismissed at this OR a more-urgent (smaller number) milestone, hide.
  // Otherwise show — and let them dismiss this stage's banner.
  if (dismissedMilestone !== null && dismissedMilestone <= currentMilestone) return null;

  const isUrgent = daysRemaining <= 3;
  const label =
    daysRemaining <= 0
      ? 'Your trial expires today'
      : daysRemaining === 1
      ? '1 day left in your free trial'
      : `${daysRemaining} days left in your free trial`;

  const handleDismiss = () => {
    setDismissedMilestone(currentMilestone);
    try { localStorage.setItem(dismissKey(firm?.id), String(currentMilestone)); }
    catch { /* ignore */ }
  };

  return (
    <>
      <div
        className={`flex items-center justify-between gap-3 px-4 py-2.5 text-sm
          ${isUrgent
            ? 'bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/50'
            : 'bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/50'
          }`}
      >
        <div className="flex items-center gap-2">
          <Clock
            size={14}
            className={isUrgent ? 'text-red-500 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}
          />
          <span className={`font-medium ${isUrgent ? 'text-red-700 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'}`}>
            {label}
          </span>
          <span className={`hidden sm:inline ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
            — upgrade to keep your matters, time, billing and client portal.
          </span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowUpgrade(true)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition
              ${isUrgent
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}
          >
            <Zap size={11} />
            Upgrade now
          </button>
          <button
            onClick={handleDismiss}
            className={`p-1 rounded hover:bg-black/10 transition ${isUrgent ? 'text-red-500' : 'text-amber-600'}`}
            title={daysRemaining > 1
              ? 'Hide — we\'ll remind you again as the deadline approaches.'
              : 'Hide for today.'}
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </>
  );
}
